import {
  createFileRoute,
  stripSearchParams,
  useNavigate,
} from "@tanstack/react-router";
import { APIProvider } from "@vis.gl/react-google-maps";
import { ListIcon, MapIcon, Pencil, SlidersHorizontal } from "lucide-react";
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { ImageWithTextAd } from "~/components/Ads/ImageWithTextAd";
import { TextOnlyAd } from "~/components/Ads/TextOnlyAd";
import { LocationCard } from "~/components/Location/LocationCard";
import { MapView } from "~/components/Search/MapView";
import { OpenMapCta } from "~/components/Search/OpenMapCta";
import {
  NumberOfGuestsOptions,
  SearchBar,
} from "~/components/Search/SearchBar";
import { SearchFilterDialog } from "~/components/Search/SearchFilterDialog";
import { SearchSettingsControl } from "~/components/SearchSettingsControl";
import { SearchSettingsDrawer } from "~/components/SearchSettingsDrawer";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { DialogTrigger } from "~/components/ui/dialog";
import { PaginationBar } from "~/components/ui/pagination-bar";
import { ScrollArea } from "~/components/ui/scroll-area";
import { LocationCardSkeleton } from "~/components/Location/LocationCardSkeleton";
import { InfoIcon } from "~/icons/InfoIcon";
import { useDebounce } from "~/lib/hooks/useDebounce";
import {
  useAddLocationToWatchListMutation,
  useRemoveLocationFromWatchListMutation,
} from "~/lib/tanstack-query/hooks/mutations/watchlist";
import { useAds } from "~/lib/tanstack-query/hooks/useAds";
import { useEventTypes } from "~/lib/tanstack-query/hooks/useEventTypes";
import { useGetGeocodedAddress } from "~/lib/tanstack-query/hooks/useGeocoding";
import { useHotelTypes } from "~/lib/tanstack-query/hooks/useHotelTypes";
import { useInfraTypes } from "~/lib/tanstack-query/hooks/useInfraTypes";
import {
  useAllMatchingMarkers,
  usePaginatedLocations,
} from "~/lib/tanstack-query/hooks/useLocations";
import { useLocationTypes } from "~/lib/tanstack-query/hooks/useLocationTypes";
import { useCheckIfLocationsAreInWatchList } from "~/lib/tanstack-query/hooks/useWatchlist";
import { resolveSearchLocationLabels } from "~/lib/server-functions/resolve-search-location-labels";
import { reportMapsClientEvent } from "~/lib/maps/client-telemetry";
import {
  alignPlaceIds,
  placeIdsForUrl,
  removeLocationSelectionAt,
} from "~/lib/search/location-place-ids";
import { cn } from "~/lib/utils";
import {
  countSetFilters,
  SearchFilterSchema,
  SearchParams,
  SearchParamsSchema,
  SearchSort,
} from "~/types/search";
import {
  eventTypesSequence,
  hotelTypesSequence,
  infraTypesSequence,
} from "~/utils/sequences";

export const Route = createFileRoute("/__main/$locale/search")({
  component: RouteComponent,
  ssr: false,
  validateSearch: SearchParamsSchema,
  search: {
    middlewares: [stripSearchParams(SearchParamsSchema.parse({}))],
  },
  pendingComponent: SearchLoadingFallback,
});

function SearchLoadingFallback() {
  return (
    <div className="layout-place-full-width h-[calc(100vh-72px)] flex items-center justify-center bg-[#eaeaea]">
      Lade Ergebnisse…
    </div>
  );
}

function RouteComponent() {
  const navigate = useNavigate({ from: Route.fullPath });
  const searchParams = Route.useSearch();
  const [searchMode, setSearchMode] = useState<"map" | "term">("term");
  const [bounds, setBounds] = useState<google.maps.LatLngBounds | null>(null);
  const [pendingBounds, setPendingBounds] =
    useState<google.maps.LatLngBounds | null>(null);
  const debouncedPendingBounds = useDebounce(pendingBounds, 300);
  const [zoom, setZoom] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  // On mobile: expanded by default, toggles based on scroll (not buttons)
  const [mobileSearchSettingsOpen, setMobileSearchSettingsOpen] =
    useState(true);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [filterExpandAccordion, setFilterExpandAccordion] = useState<
    string | undefined
  >(undefined);
  const pageSize = 50;
  // Stay on term search until MapView reports real bounds (OpenMapCta must not drive map mode).
  const [isMapFilterActive, setIsMapFilterActive] = useState(false);
  const mapModeBootstrappedRef = useRef(false);
  const latestMapBoundsRef = useRef<google.maps.LatLngBounds | null>(null);

  const [activeMarkerId, setActiveMarkerId] = useState<number>();
  const [hoveredMarkerId, setHoveredMarkerId] = useState<number>();
  const locationCardRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  // Track if we should delay showing details (for better scroll performance)
  const [pendingActiveMarkerId, setPendingActiveMarkerId] = useState<
    number | undefined
  >();

  // Scroll to location card first, then show details
  useEffect(() => {
    if (pendingActiveMarkerId && scrollAreaRef.current) {
      const cardElement = locationCardRefs.current.get(pendingActiveMarkerId);
      if (cardElement) {
        // Use requestAnimationFrame to avoid blocking the main thread
        requestAnimationFrame(() => {
          // Find the scrollable container (ScrollArea's viewport)
          const scrollContainer = scrollAreaRef.current?.querySelector(
            "[data-radix-scroll-area-viewport]",
          ) as HTMLElement;
          if (scrollContainer && cardElement) {
            // Cache rects to avoid multiple layout calculations
            const cardRect = cardElement.getBoundingClientRect();
            const containerRect = scrollContainer.getBoundingClientRect();
            const scrollTop = scrollContainer.scrollTop;
            // Calculate the absolute position of the card relative to the scroll container's content
            // cardRect.top is relative to viewport, containerRect.top is relative to viewport
            // We need the card's position relative to the scroll container's content
            const cardTopRelativeToViewport = cardRect.top;
            const containerTopRelativeToViewport = containerRect.top;
            // The difference gives us the offset within the visible area
            const offsetInVisibleArea =
              cardTopRelativeToViewport - containerTopRelativeToViewport;
            // Add current scroll to get absolute position in scroll content
            const absolutePositionInContent = scrollTop + offsetInVisibleArea;
            // Scroll to top edge - ensure the card's top edge is at the top of the visible area
            const targetScroll = Math.max(0, absolutePositionInContent);

            // Use requestAnimationFrame again for smooth scroll initiation
            requestAnimationFrame(() => {
              scrollContainer.scrollTo({
                top: targetScroll,
                behavior: "smooth",
              });

              // After scrolling starts, show details (delay to let scroll begin and avoid layout shift)
              setTimeout(() => {
                setActiveMarkerId(pendingActiveMarkerId);
                setPendingActiveMarkerId(undefined);

                // After details are shown, scroll again to ensure top edge is visible
                // (card might have grown after opening details)
                setTimeout(() => {
                  const updatedCardElement = locationCardRefs.current.get(
                    pendingActiveMarkerId,
                  );
                  const updatedScrollContainer =
                    scrollAreaRef.current?.querySelector(
                      "[data-radix-scroll-area-viewport]",
                    ) as HTMLElement;
                  if (updatedScrollContainer && updatedCardElement) {
                    const cardRect = updatedCardElement.getBoundingClientRect();
                    const containerRect =
                      updatedScrollContainer.getBoundingClientRect();
                    const scrollTop = updatedScrollContainer.scrollTop;
                    const cardTopRelativeToViewport = cardRect.top;
                    const containerTopRelativeToViewport = containerRect.top;
                    const offsetInVisibleArea =
                      cardTopRelativeToViewport -
                      containerTopRelativeToViewport;
                    const absolutePositionInContent =
                      scrollTop + offsetInVisibleArea;

                    // Only scroll if card is not fully visible at the top
                    if (offsetInVisibleArea < 0) {
                      updatedScrollContainer.scrollTo({
                        top: Math.max(0, absolutePositionInContent),
                        behavior: "smooth",
                      });
                    }
                  }
                }, 100);
              }, 150);
            });
          } else {
            // If scroll container not found, show details immediately
            setActiveMarkerId(pendingActiveMarkerId);
            setPendingActiveMarkerId(undefined);
          }
        });
      } else {
        // If card element not found, show details immediately
        setActiveMarkerId(pendingActiveMarkerId);
        setPendingActiveMarkerId(undefined);
      }
    }
  }, [pendingActiveMarkerId]);

  // Also scroll when activeMarkerId changes directly (e.g., from map click)
  // This ensures the card is scrolled to top even if not using pendingActiveMarkerId
  useEffect(() => {
    if (activeMarkerId && scrollAreaRef.current && !pendingActiveMarkerId) {
      const cardElement = locationCardRefs.current.get(activeMarkerId);
      if (cardElement) {
        // Use requestAnimationFrame to ensure DOM is updated
        requestAnimationFrame(() => {
          const scrollContainer = scrollAreaRef.current?.querySelector(
            "[data-radix-scroll-area-viewport]",
          ) as HTMLElement;
          if (scrollContainer && cardElement) {
            const cardRect = cardElement.getBoundingClientRect();
            const containerRect = scrollContainer.getBoundingClientRect();
            const scrollTop = scrollContainer.scrollTop;
            const cardTopRelativeToViewport = cardRect.top;
            const containerTopRelativeToViewport = containerRect.top;
            const offsetInVisibleArea =
              cardTopRelativeToViewport - containerTopRelativeToViewport;
            const absolutePositionInContent = scrollTop + offsetInVisibleArea;

            // Only scroll if card is not at the top edge or is cut off
            if (offsetInVisibleArea < 0 || offsetInVisibleArea > 10) {
              scrollContainer.scrollTo({
                top: Math.max(0, absolutePositionInContent),
                behavior: "smooth",
              });
            }
          }
        });
      }
    }
  }, [activeMarkerId, pendingActiveMarkerId]);

  // Helper function to scroll to active marker's card
  const scrollToActiveMarkerCard = useCallback(() => {
    if (!activeMarkerId || !scrollAreaRef.current) return;

    // Add a small delay to allow markers to be fully re-rendered
    setTimeout(() => {
      const cardElement = locationCardRefs.current.get(activeMarkerId);
      if (cardElement) {
        requestAnimationFrame(() => {
          const scrollContainer = scrollAreaRef.current?.querySelector(
            "[data-radix-scroll-area-viewport]",
          ) as HTMLElement;
          if (scrollContainer && cardElement) {
            const cardRect = cardElement.getBoundingClientRect();
            const containerRect = scrollContainer.getBoundingClientRect();
            const scrollTop = scrollContainer.scrollTop;
            const cardTopRelativeToViewport = cardRect.top;
            const containerTopRelativeToViewport = containerRect.top;
            const offsetInVisibleArea =
              cardTopRelativeToViewport - containerTopRelativeToViewport;
            const absolutePositionInContent = scrollTop + offsetInVisibleArea;

            // Only scroll if card is not at the top edge or is cut off
            if (offsetInVisibleArea < 0 || offsetInVisibleArea > 10) {
              scrollContainer.scrollTo({
                top: Math.max(0, absolutePositionInContent),
                behavior: "smooth",
              });
            }
          }
        });
      }
    }, 300); // Delay to allow marker re-render to complete after map idle
  }, [activeMarkerId]);

  const handleMapIdle = useCallback(() => {
    if (activeMarkerId && !pendingActiveMarkerId && !pendingBounds) {
      scrollToActiveMarkerCard();
    }
  }, [
    activeMarkerId,
    pendingActiveMarkerId,
    pendingBounds,
    scrollToActiveMarkerCard,
  ]);

  // Update bounds when debounced pending bounds change (triggers location search)
  // This ensures the search is triggered even on the first drag, with debounce to prevent too many API calls
  useEffect(() => {
    if (isMapFilterActive && debouncedPendingBounds) {
      setBounds(debouncedPendingBounds);
      setPendingBounds(null);
    }
  }, [isMapFilterActive, debouncedPendingBounds]);

  // Map column toggle (desktop on by default). Real Google Map loads only after CTA.
  const [showMap, setShowMap] = useState(() => {
    if (typeof window !== "undefined") {
      return window.innerWidth >= 1024;
    }
    return true;
  });
  // Keep MapView mounted after first CTA open so collapse/reopen does not rebill.
  const [mapMounted, setMapMounted] = useState(false);
  // Read by the map's bounds callback. Synced in an effect rather than during
  // render so it always reflects committed visibility; a render that React
  // discards must not leave the callback acting on state the user never saw.
  const showMapRef = useRef(showMap);
  useEffect(() => {
    showMapRef.current = showMap;
  }, [showMap]);
  const [isMapMaximized, setIsMapMaximized] = useState(false);

  const shouldUseBounds = isMapFilterActive;

  const {
    data: paginatedLocations,
    isPending: isPendingPaginatedLocations,
    isFetching: isFetchingLocations,
  } = usePaginatedLocations(
    shouldUseBounds ? bounds : null,
    searchParams,
    currentPage,
    pageSize,
  );

  // Markers only exist for the map. Resolving them re-runs server-side Place
  // Details / Geocoding for every location chip, so keep it off the wire until
  // the user opens the map. `mapMounted` stays true afterwards, so collapsing
  // and reopening the map neither remounts the map nor refetches this.
  const { data: allMarkers } = useAllMatchingMarkers(
    shouldUseBounds ? bounds : null,
    searchParams,
    { enabled: mapMounted },
  );

  const { data: watchListCheck } = useCheckIfLocationsAreInWatchList(
    paginatedLocations?.locations.map((l) => l.id) ?? [],
  );
  const addLocationToWatchListMutation = useAddLocationToWatchListMutation();
  const removeLocationFromWatchListMutation =
    useRemoveLocationFromWatchListMutation();

  const foundLocations = paginatedLocations?.locations ?? [];
  const currentPageIds = useMemo(
    () => new Set(foundLocations.map((l) => l.id)),
    [foundLocations],
  );

  const allMarkerIndexMap = useMemo(
    () => new globalThis.Map(allMarkers?.map((m, i) => [m.id, i]) ?? []),
    [allMarkers],
  );

  const pendingScrollAfterPageChange = useRef<number | undefined>(undefined);

  // After a page change triggered by marker click, scroll to the target card once data loads
  useEffect(() => {
    const targetId = pendingScrollAfterPageChange.current;
    if (targetId && paginatedLocations) {
      const isOnPage = paginatedLocations.locations.some(
        (l) => l.id === targetId,
      );
      if (isOnPage) {
        requestAnimationFrame(() => {
          setPendingActiveMarkerId(targetId);
          pendingScrollAfterPageChange.current = undefined;
        });
      }
    }
  }, [paginatedLocations]);

  const { data: eventTypes } = useEventTypes(eventTypesSequence);
  const { data: locationTypes } = useLocationTypes();
  const { data: infraTypes } = useInfraTypes(infraTypesSequence);
  const { data: hotelTypes } = useHotelTypes(hotelTypesSequence);
  const { data: ads } = useAds();

  const totalMatchingLocations =
    paginatedLocations?.totalMatching ?? paginatedLocations?.total ?? 0;

  // Reset page when search mode changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchMode]);

  useEffect(() => {
    if (isMapFilterActive) {
      setCurrentPage(1);
    }
  }, [bounds?.toUrlValue(), zoom, isMapFilterActive]);

  // Keep latest committed search params for async CMS resolve (avoid stomping concurrent edits).
  const searchParamsRef = useRef(searchParams);
  useEffect(() => {
    searchParamsRef.current = searchParams;
  }, [searchParams]);

  // CMS prefill: rewrite ?occasion=10&loc=Berlin&locationType=3 to canonical filters URL.
  // Resolve loc labels to placeIds so search uses Place Details instead of Geocoding.
  useEffect(() => {
    const occasion = searchParams.occasion?.trim();
    const loc = searchParams.loc?.trim();
    const locPlaceIds = searchParams.locPlaceIds?.trim();
    const locationType = searchParams.locationType?.trim();
    if (!occasion && !loc && !locPlaceIds && !locationType) return;

    let cancelled = false;

    const defaultFilters = SearchFilterSchema.parse({});
    const occasionIds = occasion
      ? occasion
          .split(",")
          .map((s) => parseInt(s.trim(), 10))
          .filter((n) => !Number.isNaN(n))
      : [];
    const locationTypeIds = locationType
      ? locationType
          .split(",")
          .map((s) => parseInt(s.trim(), 10))
          .filter((n) => !Number.isNaN(n))
      : [];
    const locationNames = loc
      ? loc
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
      : [];
    const cmsPlaceIds = locPlaceIds
      ? locPlaceIds.split(",").map((value) => value.trim())
      : [];

    void (async () => {
      const started = searchParamsRef.current;
      const startedLocationKey = JSON.stringify(started.location ?? []);
      const startedPlaceIdsKey = JSON.stringify(started.placeIds ?? []);

      let location = started.location;
      let placeIds = started.placeIds;

      if (locationNames.length > 0) {
        const canonicalCmsLabels = SearchParamsSchema.safeParse({
          location: locationNames,
        });
        const canonicalCmsSelection = SearchParamsSchema.safeParse({
          location: locationNames,
          placeIds: cmsPlaceIds,
        });
        if (
          cmsPlaceIds.every(Boolean) &&
          canonicalCmsSelection.success
        ) {
          location = canonicalCmsSelection.data.location;
          placeIds = placeIdsForUrl(
            canonicalCmsSelection.data.placeIds ?? [],
          );
        } else if (canonicalCmsLabels.success) {
          try {
            const resolved = await resolveSearchLocationLabels({
              data: { labels: canonicalCmsLabels.data.location },
            });
            if (cancelled) return;
            location = resolved.location;
            placeIds = placeIdsForUrl(resolved.placeIds);
          } catch (error) {
            console.error("Failed to resolve CMS loc to placeIds:", error);
            if (cancelled) return;
            // Fall back to free-text location chips (server may geocode).
            location = canonicalCmsLabels.data.location;
            placeIds = undefined;
          }
        } else {
          console.warn("Ignored invalid CMS location parameters");
        }
      }

      if (cancelled) return;

      const current = searchParamsRef.current;
      const {
        occasion: _o,
        loc: _l,
        locPlaceIds: _lp,
        locationType: _lt,
        ...currentRest
      } = current;

      // User changed location chips while resolve was in flight — keep theirs.
      const locationChanged =
        JSON.stringify(current.location ?? []) !== startedLocationKey ||
        JSON.stringify(current.placeIds ?? []) !== startedPlaceIdsKey;

      navigate({
        search: {
          ...currentRest,
          filters: {
            ...(currentRest.filters ?? defaultFilters),
            ...(occasionIds.length > 0 && { occasion: occasionIds }),
            ...(locationTypeIds.length > 0 && {
              locationType: locationTypeIds,
            }),
          },
          ...(locationChanged
            ? {
                location: current.location,
                placeIds: current.placeIds,
              }
            : { location, placeIds }),
        },
        replace: true,
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [
    searchParams.occasion,
    searchParams.loc,
    searchParams.locPlaceIds,
    searchParams.locationType,
    navigate,
  ]);

  // Deactivate map filter and set search mode to term when location is set.
  // When location chips are cleared, re-enable map filter only if MapView already
  // bootstrapped map mode (not while OpenMapCta is the only map UI).
  useEffect(() => {
    if (searchParams.location && searchParams.location.length > 0) {
      setIsMapFilterActive(false);
      setSearchMode("term");
    } else if (
      mapModeBootstrappedRef.current &&
      latestMapBoundsRef.current
    ) {
      // Apply retained bounds immediately (skip debounce) before queries use map filter.
      setBounds(latestMapBoundsRef.current);
      setIsMapFilterActive(true);
      setSearchMode("map");
    }
  }, [searchParams.location]);

  // Reset active marker when page changes, unless navigating to a specific marker
  useEffect(() => {
    if (!pendingScrollAfterPageChange.current) {
      setActiveMarkerId(undefined);
    }
  }, [currentPage]);

  // Update search settings height based on visibility
  useEffect(() => {
    const updateSearchSettingsHeight = () => {
      const isDesktop = window.innerWidth >= 768; // md breakpoint
      const isOpen = isDesktop || mobileSearchSettingsOpen;
      const height = isOpen ? "248px" : "90px";
      document.documentElement.style.setProperty(
        "--search-settings-height",
        height,
      );
    };

    updateSearchSettingsHeight();
    window.addEventListener("resize", updateSearchSettingsHeight);
    return () =>
      window.removeEventListener("resize", updateSearchSettingsHeight);
  }, [mobileSearchSettingsOpen]);

  // On mobile: collapsed bar visible when expanded scrolls out of view.
  const expandedSearchRef = useRef<HTMLDivElement>(null);
  const hasBeenInViewRef = useRef(false);

  useEffect(() => {
    const el = expandedSearchRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (window.innerWidth >= 768) return;
        if (entry.isIntersecting) {
          hasBeenInViewRef.current = true;
          setMobileSearchSettingsOpen(true);
        } else if (hasBeenInViewRef.current) {
          setMobileSearchSettingsOpen(false);
        }
      },
      { threshold: 0 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const setSearchParams = (params: SearchParams) => {
    // Check if filters are empty
    const hasFilters =
      params.filters &&
      (params.filters.occasion.length > 0 ||
        params.filters.locationType.length > 0 ||
        params.filters.seating !== "" ||
        params.filters.locationSettings.indoor ||
        params.filters.locationSettings.outdoor ||
        params.filters.locationSettings.mixed ||
        params.filters.locationSize.maxArea > 0 ||
        params.filters.locationSize.indoorAreaTotal > 0 ||
        params.filters.locationSize.maxHeight > 0 ||
        params.filters.locationOpening > 0 ||
        params.filters.locationRenovation > 0 ||
        params.filters.commonProperties.accessible ||
        params.filters.commonProperties.dimmable ||
        params.filters.commonProperties.daylight ||
        (params.filters.numberOfNeededRooms ?? 0) > 0 ||
        params.filters.outerAreaSize > 0 ||
        params.filters.stage ||
        params.filters.numberOfParkingSpaces > 0 ||
        params.filters.parkingLots !== undefined ||
        params.filters.hasAccommodations !== undefined ||
        params.filters.hotelRooms > 0 ||
        params.filters.hotelStars > 0 ||
        (params.filters.hotelTypes?.length ?? 0) > 0 ||
        params.filters.budget?.type !== undefined ||
        (params.filters.budget &&
          (params.filters.budget.from !== null ||
            params.filters.budget.to !== null)) ||
        params.filters.cateringBinding !== "" ||
        params.filters.techBinding !== "" ||
        params.filters.infrastructure.length > 0 ||
        params.filters.distances.length > 0 ||
        params.filters.rentalDuration.days.length > 0 ||
        params.filters.rentalDuration.from !== null ||
        params.filters.rentalDuration.to !== null ||
        params.filters.deliveryLogistics?.truckAccess ||
        params.filters.deliveryLogistics?.carAccess ||
        params.filters.deliveryLogistics?.freightElevator ||
        params.filters.deliveryLogistics?.loadingRamp ||
        params.filters.leisureSportWellness?.leisure?.sightseeing ||
        params.filters.leisureSportWellness?.sport?.golf ||
        params.filters.leisureSportWellness?.sport?.tennis ||
        params.filters.leisureSportWellness?.sport?.sailing ||
        params.filters.leisureSportWellness?.sport?.ski ||
        params.filters.leisureSportWellness?.wellness?.sauna ||
        params.filters.leisureSportWellness?.wellness?.massage ||
        params.filters.leisureSportWellness?.wellness?.indoorPool ||
        params.filters.leisureSportWellness?.wellness?.outdoorPool ||
        params.filters.certifications?.gstcHotel ||
        params.filters.certifications?.greenKey ||
        params.filters.certifications?.iso14001 ||
        params.filters.equipment?.acPower?.volt110 ||
        params.filters.equipment?.acPower?.volt230 ||
        params.filters.equipment?.threePhasePower?.amp16 ||
        params.filters.equipment?.threePhasePower?.amp32 ||
        params.filters.equipment?.threePhasePower?.amp64 ||
        params.filters.equipment?.threePhasePower?.amp128 ||
        params.filters.equipment?.technology?.lighting ||
        params.filters.equipment?.technology?.presentationVideo ||
        params.filters.equipment?.technology?.audio ||
        params.filters.equipment?.technology?.heatingClimate ||
        params.filters.equipment?.specialTechnology?.rigging ||
        params.filters.equipment?.specialTechnology?.v1 ||
        params.filters.equipment?.specialTechnology?.v2 ||
        params.filters.equipment?.specialTechnology?.v3 ||
        params.filters.equipment?.specialTechnology?.v4 ||
        params.filters.equipment?.specialTechnology?.v5 ||
        params.filters.equipment?.internet?.g5 ||
        params.filters.equipment?.internet?.g4 ||
        params.filters.equipment?.internet?.g3 ||
        params.filters.equipment?.internet?.g2 ||
        (params.filters.equipment?.internet?.minBandwidth ?? 0) > 0 ||
        params.filters.equipment?.furniture !== "none" ||
        params.filters.equipment?.sanitary?.wcWomen ||
        params.filters.equipment?.sanitary?.wcMen ||
        params.filters.equipment?.sanitary?.wcAccessible ||
        params.filters.equipment?.other?.smokingArea ||
        params.filters.equipment?.other?.artistDressingRooms ||
        params.filters.equipment?.other?.noNoiseRestrictions ||
        params.filters.equipment?.other?.shower ||
        params.filters.equipment?.other?.backstageArea ||
        params.filters.equipment?.other?.danceFloor);

    navigate({
      search: {
        ...params,
        filters: hasFilters ? { ...params.filters } : undefined,
      },
    });
  };
  // Search-area resolution is performed once by the paginated server query and
  // reused here instead of issuing a second Place Details/Geocoding request.
  const primaryLocationResolution =
    paginatedLocations?.primaryLocationResolution ?? null;
  const unresolvedMapLocation =
    searchParams.location?.[0]?.trim() ?? "";
  // State-code searches intentionally avoid geocoding in the SQL filter. They
  // still need one map-only geocode because no server resolution is returned.
  const { data: fallbackMapResolution } = useGetGeocodedAddress(
    unresolvedMapLocation,
    {
      source: "search_map_center",
      enabled:
        mapMounted &&
        paginatedLocations !== undefined &&
        !primaryLocationResolution,
    },
  );

  const defaultFilters = {
    occasion: [],
    locationType: [],
    seating: "",
    locationSettings: { indoor: false, outdoor: false, mixed: false },
    locationSize: { maxArea: 0, indoorAreaTotal: 0, maxHeight: 0 },
    locationOpening: 0,
    locationRenovation: 0,
    commonProperties: { accessible: false, dimmable: false, daylight: false },
    numberOfNeededRooms: undefined,
    outerAreaSize: 0,
    stage: false,
    numberOfParkingSpaces: 0,
    parkingLots: undefined,
    hasAccommodations: undefined,
    hotelRooms: 0,
    hotelStars: 0,
    hotelTypes: [],
    budget: undefined,
    cateringBinding: "" as const,
    techBinding: "" as const,
    infrastructure: [],
    distances: [],
    rentalDuration: {
      days: [],
      from: null,
      to: null,
    },
    deliveryLogistics: {
      truckAccess: false,
      carAccess: false,
      freightElevator: false,
      loadingRamp: false,
    },
    leisureSportWellness: {
      leisure: {
        sightseeing: false,
      },
      sport: {
        golf: false,
        tennis: false,
        sailing: false,
        ski: false,
      },
      wellness: {
        sauna: false,
        massage: false,
        indoorPool: false,
        outdoorPool: false,
      },
    },
    certifications: {
      gstcHotel: false,
      greenKey: false,
      iso14001: false,
    },
    equipment: {
      acPower: {
        volt110: false,
        volt230: false,
      },
      threePhasePower: {
        amp16: false,
        amp32: false,
        amp64: false,
        amp128: false,
      },
      technology: {
        lighting: false,
        presentationVideo: false,
        audio: false,
        heatingClimate: false,
      },
      specialTechnology: {
        rigging: false,
        v1: false,
        v2: false,
        v3: false,
        v4: false,
        v5: false,
      },
      internet: {
        g5: false,
        g4: false,
        g3: false,
        g2: false,
        minBandwidth: 0,
      },
      furniture: "none" as const,
      sanitary: {
        wcWomen: false,
        wcMen: false,
        wcAccessible: false,
      },
      other: {
        smokingArea: false,
        artistDressingRooms: false,
        noNoiseRestrictions: false,
        shower: false,
        backstageArea: false,
        danceFloor: false,
      },
    },
  };
  const numberOfActiveFilters = countSetFilters(
    searchParams.filters ?? defaultFilters,
    searchParams.type,
    searchParams.guests,
    searchParams.locationIdOrName,
    searchParams.location,
  );

  // Filter out invalid image-text ads (those without imageIdentifier)
  const validAds = useMemo(() => {
    if (!ads) return [];
    return ads.filter((ad) => {
      // Filter out image-text ads that don't have an imageIdentifier
      if (ad.type === "image-text" && !ad.imageIdentifier) {
        return false;
      }
      return true;
    });
  }, [ads]);

  // Helper function to generate a deterministic pseudo-random number based on seed
  const seededRandom = (seed: number) => {
    // Simple seeded random function for deterministic selection
    const x = Math.sin(seed) * 10000;
    return x - Math.floor(x);
  };

  // Memoized function to interleave ads with locations
  const itemsWithAds = useMemo(() => {
    if (!validAds || validAds.length === 0) {
      return foundLocations.map((location, index) => ({
        type: "location" as const,
        location,
        index,
      }));
    }

    const items: Array<
      | {
        type: "location";
        location: (typeof foundLocations)[0];
        index: number;
      }
      | { type: "ad"; ad: (typeof validAds)[0] }
    > = [];

    const minInterval = 7;
    const maxInterval = 10; // Maximum interval for randomization
    let locationsSinceLastAd = 0;
    // Use a seed based on the first location's ID for deterministic intervals
    const baseSeed = foundLocations[0]?.id ?? 0;
    let nextAdInterval =
      minInterval +
      Math.floor(seededRandom(baseSeed) * (maxInterval - minInterval + 1));

    foundLocations.forEach((location, index) => {
      items.push({ type: "location", location, index });
      locationsSinceLastAd++;

      // Insert ad when we reach the randomized interval (minimum 7)
      if (locationsSinceLastAd >= nextAdInterval) {
        // Deterministically select an ad based on location index
        // This ensures the same location always gets the same ad
        const adSeed = baseSeed + index;
        const adIndex = Math.floor(seededRandom(adSeed) * validAds.length);
        const selectedAd = validAds[adIndex];
        if (selectedAd) {
          items.push({ type: "ad", ad: selectedAd });
        }
        // Reset counter and generate new deterministic interval
        locationsSinceLastAd = 0;
        const intervalSeed = baseSeed + index + 1000; // Offset to get different value
        nextAdInterval =
          minInterval +
          Math.floor(
            seededRandom(intervalSeed) * (maxInterval - minInterval + 1),
          );
      }
    });

    return items;
  }, [foundLocations, validAds]);

  const collapsedBar = (
    <div
      className={cn(
        "md:hidden fixed top-0 left-0 right-0 z-10 mx-2 mt-1 flex flex-col justify-center",
        "min-h-[45px] border border-neutral-300 bg-neutral-50 py-2 px-3 rounded-md text-sm shadow-lg",
        "transition-[opacity,transform] duration-200",
        mobileSearchSettingsOpen &&
        "opacity-0 pointer-events-none -translate-y-3",
      )}
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
    >
      {(() => {
        const hasLocation =
          searchParams.location && searchParams.location.length > 0;
        const hasGuests = searchParams.guests && searchParams.guests !== "any";
        const hasType = searchParams.type
          ? eventTypes?.find((t) => t.id === searchParams.type)?.name
          : null;
        const isEmpty = !hasLocation && !hasGuests && !hasType;

        if (isEmpty) {
          return (
            <p className="font-medium text-neutral-600">
              Suchkriterien einstellen
            </p>
          );
        }

        return (
          <>
            <p className="font-medium">
              {hasLocation ? searchParams.location!.join(", ") : ""}
            </p>
            <p className="flex flex-wrap gap-3">
              {hasGuests && (
                <span className="text-neutral-600">
                  {
                    NumberOfGuestsOptions[
                    searchParams.guests as keyof typeof NumberOfGuestsOptions
                    ]
                  }{" "}
                  Personen gesamt
                </span>
              )}
              {hasType && <span className="text-neutral-600">{hasType}</span>}
            </p>
          </>
        );
      })()}
      <Pencil className="size-4 text-neutral-600 absolute top-1/2 -translate-y-1/2 right-3" />
    </div>
  );

  return (
    <div className="layout-place-full-width bg-[#eaeaea] [&>div+div]:mt-0">
      {/* Expanded: static, scrolls with page */}
      <div
        ref={expandedSearchRef}
        className="layout-content-grid pt-3 md:shadow-lg pb-3 lg:min-h-[240px] mb-2 gap-y-0"
      >
        <SearchBar
          className="w-full"
          location={searchParams.location}
          placeIds={searchParams.placeIds}
          guests={searchParams.guests}
          type={searchParams.type}
          setLocationSearch={(location, placeIds) =>
            setSearchParams({ ...searchParams, location, placeIds })
          }
          setGuests={(guests) => setSearchParams({ ...searchParams, guests })}
          setType={(type) => setSearchParams({ ...searchParams, type })}
          numberOfMatchingLocations={totalMatchingLocations}
          isPending={isPendingPaginatedLocations}
        />
        <div className="mt-2 md:mt-6 flex items-center-safe flex-wrap gap-2 md:gap-6">
          <span>
            {totalMatchingLocations} Veranstaltungsräume/-flächen zur Suche in
          </span>
          {(!searchParams.location || searchParams.location.length === 0) &&
            (!searchParams.guests || searchParams.guests === "any") &&
            !Array.from(
              new Set([
                searchParams.type,
                ...(searchParams.filters?.occasion ?? []),
              ]),
            ).length ? (
            <span className="text-muted-foreground italic">
              Keine Filter gesetzt
            </span>
          ) : null}
          {searchParams.location &&
            searchParams.location.length > 0 &&
            searchParams.location.map((loc, index) => (
              <Badge
                key={index}
                onClose={() => {
                  const next = removeLocationSelectionAt(
                    searchParams.location,
                    searchParams.placeIds,
                    index,
                  );
                  setSearchParams({
                    ...searchParams,
                    location: next.location,
                    placeIds: placeIdsForUrl(next.placeIds),
                  });
                }}
              >
                {loc}
              </Badge>
            ))}
          {!!searchParams.guests && searchParams.guests !== "any" && (
            <Badge
              onClose={() => setSearchParams({ ...searchParams, guests: "" })}
            >
              {NumberOfGuestsOptions[searchParams.guests]} Personen gesamt
            </Badge>
          )}
          {Array.from(
            new Set([
              searchParams.type,
              ...(searchParams.filters?.occasion ?? []),
            ]),
          ).map((occasionId) => {
            const occasion = eventTypes?.find((t) => t.id === occasionId);

            if (!occasion) return null;

            return (
              <Badge
                key={occasionId}
                onClose={() => {
                  const filters = searchParams.filters ?? defaultFilters;

                  setSearchParams({
                    ...searchParams,
                    filters: {
                      ...filters,
                      occasion: filters.occasion.filter(
                        (id) => id !== occasionId,
                      ),
                    },
                    type:
                      searchParams.type === occasionId
                        ? null
                        : searchParams.type,
                  });
                }}
              >
                {occasion.name}
              </Badge>
            );
          })}
        </div>
        <div>
          <span className="mt-2 inline-block text-sm text-[#6c6c6b]">
            Der Suchradius beträgt {searchParams.searchRadius ?? 20} km und kann
            in den{" "}
            <button
              type="button"
              className="cursor-pointer underline text-[#6c6c6b] hover:text-[#333] focus:outline-none focus:ring-2 focus:ring-[#6c6c6b] focus:ring-offset-1 rounded"
              onClick={() => {
                setFilterExpandAccordion("filter-location-search");
                setFilterDialogOpen(true);
              }}
            >
              erweiterten Filtern
            </button>{" "}
            angepasst werden.
          </span>
        </div>
        <div className="flex flex-wrap items-end justify-between">
          <SearchSettingsDrawer>
            <div className="">
              <span className="pl-1 md:pl-4 text-sm font-medium line-clamp-1 w-full block">
                Mehr Filter
              </span>
              <SearchFilterDialog
                open={filterDialogOpen}
                onOpenChange={(open) => {
                  setFilterDialogOpen(open);
                  if (!open) setFilterExpandAccordion(undefined);
                }}
                expandAccordionOnOpen={filterExpandAccordion}
                trigger={
                  <DialogTrigger asChild>
                    <Button
                      variant="flatWhite"
                      className="w-full min-w-0"
                      id="filter-button"
                    >
                      <span className="grow min-w-0 overflow-hidden text-left flex items-center gap-2">
                        <span className="min-w-0 truncate">
                          <span className="hidden md:inline">Alle&nbsp;</span>
                          Filter<span className="md:hidden">n</span>
                        </span>
                        {numberOfActiveFilters > 0 && (
                          <Badge size={"sm"} className="rounded-full">
                            {numberOfActiveFilters}
                          </Badge>
                        )}
                      </span>
                      <SlidersHorizontal />
                    </Button>
                  </DialogTrigger>
                }
                eventTypes={(eventTypes || []).map((eventType) => ({
                  ...eventType,
                  locationCount:
                    paginatedLocations?.eventTypeCounts.find(
                      (e) => e.id === eventType.id,
                    )?.locationCount ?? 0,
                }))}
                locationTypes={(locationTypes || []).map((locationType) => ({
                  ...locationType,
                  locationCount:
                    paginatedLocations?.locationTypeCounts.find(
                      (e) => e.id === locationType.id,
                    )?.locationCount ?? 0,
                }))}
                infraTypes={(infraTypes || []).map((infraType) => ({
                  ...infraType,
                  locationCount:
                    paginatedLocations?.infraTypeCounts.find(
                      (e) => e.id === infraType.id,
                    )?.locationCount ?? 0,
                }))}
                hotelTypes={(hotelTypes || []).map((hotelType) => ({
                  ...hotelType,
                  locationCount: 0, // TODO: Add hotelTypeCounts to paginatedLocations if needed
                }))}
                numberOfMatchingLocations={totalMatchingLocations}
                value={{
                  filters: searchParams.filters ?? defaultFilters,
                  guests: searchParams.guests,
                  type: searchParams.type,
                  location: searchParams.location,
                  placeIds: searchParams.placeIds,
                  searchRadius: searchParams.searchRadius,
                  locationIdOrName: searchParams.locationIdOrName,
                }}
                onValueChange={(value) => {
                  const nextLocation = value.location ?? [];
                  setSearchParams({
                    ...searchParams,
                    ...value,
                    type: value.type ?? null,
                    location: nextLocation,
                    // The dialog always returns Place IDs aligned with its
                    // labels; keep them so search uses Place Details, not
                    // Geocoding.
                    placeIds: placeIdsForUrl(
                      alignPlaceIds(nextLocation, value.placeIds),
                    ),
                    searchRadius: value.searchRadius,
                    locationIdOrName: value.locationIdOrName,
                  });
                }}
              />
            </div>
            <SearchSettingsControl
              label="Sortiert nach"
              options={{
                recommended: "Empfohlene Locations",
                "price-asc": "Preis (aufsteigend)",
                "price-desc": "Preis (absteigend)",
                // "number-of-ratings": "Anzahl der Bewertungen",
              }}
              icon={<SlidersHorizontal />}
              value={searchParams.sort}
              onValueChange={(value) =>
                setSearchParams({
                  ...searchParams,
                  sort: value as SearchSort,
                })
              }
            />
            <SearchSettingsControl
              label="Suchmodus"
              options={{
                map: "im Kartenausschnitt suchen",
                term: `in ${searchParams.location && searchParams.location.length > 0 ? searchParams.location.join(", ") : "eingegebenem Ort"} suchen`,
              }}
              icon={<SlidersHorizontal />}
              value={searchMode}
              onValueChange={(value) => {
                const mode = value as "map" | "term";
                setSearchMode(mode);
                if (mode === "map" && latestMapBoundsRef.current) {
                  setBounds(latestMapBoundsRef.current);
                }
                setIsMapFilterActive(mode === "map");
              }}
            />
            <Button
              variant={"flatDark"}
              onClick={() => {
                setShowMap(!showMap);
                setIsMapMaximized(false);
              }}
              size={"icon"}
              className="mt-3 md:mt-0 lg:hidden h-10"
            >
              {showMap ? <ListIcon /> : <MapIcon />}
            </Button>
          </SearchSettingsDrawer>
          <Button
            variant={"flatDark"}
            onClick={() => setShowMap(!showMap)}
            className="hidden lg:inline-flex"
          >
            Karte&nbsp;
            {showMap ? "ausblenden" : "einblenden"}
          </Button>
        </div>
      </div>

      {typeof document !== "undefined" &&
        createPortal(collapsedBar, document.body)}

      <div
        className={cn(
          "flex flex-col",
          showMap && !isMapMaximized ? "lg:flex-row" : "lg:flex-col",
          "lg:h-[calc(100vh-72px-var(--search-settings-height)-var(--location-drawer-height))]",
        )}
      >
        {/* List view - hidden on mobile when map is shown, always visible on desktop when not maximized */}
        {!isMapMaximized && (
          <div
            className={cn(
              "relative overflow-hidden",
              showMap && "hidden lg:block",
              showMap &&
                !mapMounted &&
                "lg:flex-1 lg:min-w-0",
              showMap &&
                mapMounted &&
                "lg:flex-1 lg:min-w-[400px] lg:max-w-[900px]",
            )}
          >
            <ScrollArea
              ref={scrollAreaRef}
              className="h-auto min-h-0 lg:h-[calc(100vh-72px-var(--search-settings-height)-var(--location-drawer-height))] w-full"
            >
              <div className="pr-4 pl-4 pb-6">
                <div className="grid gap-2 pb-4">
                  {isFetchingLocations && foundLocations.length === 0
                    ? Array.from({ length: 6 }).map((_, i) => (
                      <LocationCardSkeleton key={i} />
                    ))
                    : null}
                  {(() => {
                    let budgetDeviatingSeparatorShown = false;
                    return itemsWithAds.map((item, index) => {
                      if (item.type === "location") {
                        const showBudgetDeviatingSeparator =
                          item.location.budgetMatches === false &&
                          !budgetDeviatingSeparatorShown;
                        if (showBudgetDeviatingSeparator) {
                          budgetDeviatingSeparatorShown = true;
                        }
                        return (
                          <div
                            key={item.location.id ?? item.index}
                            className="contents"
                          >
                            {showBudgetDeviatingSeparator && (
                              <p className="shadow rounded-md text-sm text-muted-foreground bg-white px-2 py-2 col-span-full flex items-center gap-2">
                                <InfoIcon className="size-4" />
                                Leicht abweichend von deinen Filtern –
                                vielleicht trotzdem passend für dich.
                              </p>
                            )}
                            <LocationCard
                              onSelect={(id) => {
                                if (id) {
                                  setPendingActiveMarkerId(id);
                                } else {
                                  setActiveMarkerId(undefined);
                                }
                              }}
                              onHover={setHoveredMarkerId}
                              location={item.location}
                              selectedLocationId={activeMarkerId}
                              showSelectedBorder={true}
                              cardRef={(el) => {
                                if (el) {
                                  locationCardRefs.current.set(
                                    item.location.id,
                                    el,
                                  );
                                } else {
                                  locationCardRefs.current.delete(
                                    item.location.id,
                                  );
                                }
                              }}
                              isBookmarked={watchListCheck?.includes(
                                item.location.id,
                              )}
                              onBookmark={() => {
                                if (
                                  watchListCheck?.includes(item.location.id)
                                ) {
                                  removeLocationFromWatchListMutation.mutateAsync(
                                    item.location.id,
                                  );
                                } else if (
                                  !watchListCheck?.includes(item.location.id)
                                ) {
                                  addLocationToWatchListMutation.mutateAsync(
                                    item.location.id,
                                  );
                                }
                              }}
                            />
                          </div>
                        );
                      } else {
                        // Render ad
                        return (
                          <div key={`ad-${item.ad.uid}-${index}`}>
                            {item.ad.type === "text-only" ? (
                              <TextOnlyAd ad={item.ad} />
                            ) : (
                              <ImageWithTextAd ad={item.ad} />
                            )}
                          </div>
                        );
                      }
                    });
                  })()}
                </div>
                {paginatedLocations && paginatedLocations.totalPages > 1 && (
                  <div className="pb-6">
                    <PaginationBar
                      currentPage={currentPage}
                      totalPages={paginatedLocations?.totalPages ?? 0}
                      onPageChange={setCurrentPage}
                    />
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
        )}

        {/* Map column: CTA placeholder until user activates; then real map (kept mounted). */}
        {showMap && !mapMounted && (
          <div
            className={cn(
              "pr-4 pl-2 h-[calc(100vh-72px-var(--search-settings-height))] lg:h-[calc(100vh-72px-var(--search-settings-height)-var(--location-drawer-height))]",
              "lg:w-1/3 lg:flex-none lg:min-w-0",
            )}
          >
            <OpenMapCta
              onOpenMap={() => {
                reportMapsClientEvent("map_cta_opened", "search_map");
                setMapMounted(true);
                setIsMapMaximized(false);
              }}
            />
          </div>
        )}
        {mapMounted && (
          <div
            className={cn(
              "pr-4 pl-2 h-[calc(100vh-72px-var(--search-settings-height))] lg:h-[calc(100vh-72px-var(--search-settings-height)-var(--location-drawer-height))]",
              !isMapMaximized && "lg:flex-1 lg:min-w-0",
              !showMap && "hidden",
            )}
            aria-hidden={!showMap}
          >
            <APIProvider
              apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}
              onLoad={() =>
                reportMapsClientEvent("maps_js_load_ok", "search_map")
              }
              onError={() =>
                reportMapsClientEvent("maps_js_load_error", "search_map")
              }
            >
              <MapView
                markers={allMarkers}
                pageLocations={foundLocations}
                currentPageIds={currentPageIds}
                activeMarkerId={activeMarkerId}
                hoveredMarkerId={hoveredMarkerId}
                isVisible={showMap}
                center={
                  primaryLocationResolution
                    ? {
                        lat: primaryLocationResolution.lat,
                        lng: primaryLocationResolution.lng,
                      }
                    : fallbackMapResolution
                      ? {
                          lat: fallbackMapResolution.lat,
                          lng: fallbackMapResolution.lng,
                        }
                      : null
                }
                bounds={
                  primaryLocationResolution?.bounds ||
                  primaryLocationResolution?.viewport ||
                  fallbackMapResolution?.bounds ||
                  fallbackMapResolution?.viewport ||
                  null
                }
                isMaximized={isMapMaximized}
                isMapFilterActive={isMapFilterActive}
                onBoundsChange={(b) => {
                  if (!showMapRef.current) return;
                  latestMapBoundsRef.current = b;
                  // First real MapView bounds: leave placeholder term mode for map search
                  // unless location chips already force term mode.
                  if (!mapModeBootstrappedRef.current) {
                    mapModeBootstrappedRef.current = true;
                    const hasLocationChips =
                      Array.isArray(searchParams.location) &&
                      searchParams.location.length > 0;
                    if (!hasLocationChips) {
                      setSearchMode("map");
                      setIsMapFilterActive(true);
                      setBounds(b);
                    }
                    return;
                  }
                  if (isMapFilterActive) {
                    setPendingBounds(b);
                  }
                }}
                onZoomChange={setZoom}
                onCollapse={() => {
                  setShowMap(false);
                  setIsMapMaximized(false);
                }}
                onMapFilterChange={(isActive) => {
                  if (!isActive) {
                    setPendingBounds(null);
                  } else if (latestMapBoundsRef.current) {
                    // Apply retained bounds immediately; keep debounce for live map moves only.
                    setBounds(latestMapBoundsRef.current);
                  }
                  setIsMapFilterActive(isActive);
                }}
                onMaximizeChange={setIsMapMaximized}
                onActiveMarkerIdChange={setActiveMarkerId}
                onMarkerClick={(locationId) => {
                  if (currentPageIds.has(locationId)) {
                    setPendingActiveMarkerId(locationId);
                  } else {
                    const index = allMarkerIndexMap.get(locationId);
                    if (index !== undefined) {
                      const targetPage = Math.floor(index / pageSize) + 1;
                      pendingScrollAfterPageChange.current = locationId;
                      setCurrentPage(targetPage);
                    }
                  }
                }}
                onMapIdle={handleMapIdle}
              />
            </APIProvider>
          </div>
        )}
      </div>
    </div>
  );
}
